import { useContext } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { LangContext } from "../../context/LangContext";

const Seo = () => {
    const location = useLocation();
    const { translations } = useContext(LangContext);

    const segments = location.pathname.split("/").filter(Boolean);

    const lang = segments[0] || "fr";
    const page = segments[1] || "home";
    const title_path = segments[2] || null;

    const title_name = title_path
        ? title_path.replace(/_/g, " ").replace(/^./, c => c.toUpperCase())
        : null;

    const baseUrl = "https://vanrah-sama.com";
    const currentUrl = `${baseUrl}${location.pathname}`;

    let title = "";
    let description = "";

    switch (page) {
        case "illustrations":
            title = "Illustrations originales - VanRah";
            description = lang === "fr"
                ? "Explorez les illustrations originales de VanRah : personnages, univers manga et créations exclusives issues de son travail d'artiste."
                : "Explore VanRah’s original illustrations: manga characters, unique worlds and exclusive artworks from her creative universe.";
            break;

        case "mangas":
        case "works":
            if (title_path) {
                title = `${title_name} - VanRah`;
                description = translations[`${title_path}_descrip_${lang}`] || "";
            } else {
                title = lang === "fr"
                    ? "Mangas et projets - VanRah"
                    : "Manga projects - VanRah";
                description = lang === "fr"
                    ? "Découvrez les mangas et projets créés par VanRah : séries originales, univers narratifs et œuvres illustrées."
                    : "Discover manga series and creative projects by VanRah: original stories, illustrated works and unique narrative universes.";
            }
            break;

        case "articles":
            title = `${title_name} - VanRah`;
            description = lang === "fr"
                ? "Lisez cet article pour en savoir plus sur l'univers, le processus créatif et les actualités de VanRah."
                : "Read this article to learn more about VanRah’s universe, creative process and latest updates.";
            break;

        case "shop":
            title = lang === "fr"
                ? "Boutique officielle - VanRah"
                : "Official Shop - VanRah";
            description = lang === "fr"
                ? "Retrouvez dans la boutique officielle les mangas, illustrations, éditions spéciales et produits exclusifs de VanRah."
                : "Browse the official shop for manga volumes, exclusive illustrations, special editions and original products by VanRah.";
            break;

        default:
            title = lang === "fr"
                ? "VanRah - Artiste Manga & Illustratrice"
                : "VanRah - Manga Artist & Illustrator";
            description = lang === "fr"
                ? "Site officiel de VanRah, artiste manga et illustratrice. Découvrez ses œuvres, projets, actualités et boutique."
                : "Official website of VanRah, manga artist and illustrator. Explore her works, projects, news and shop.";
    }

    const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${baseUrl}#person`,
        "name": "VanRah",
        "url": baseUrl,
        "image": `${baseUrl}/og-image.png`,
        "jobTitle": "Manga Artist",
        "sameAs": [
            "https://www.facebook.com/VanRah.Sama/",
            "https://www.instagram.com/vanrah.sama/",
            "https://www.youtube.com/@VanRah-Sama",
            "https://www.deviantart.com/vanrah",
            "https://www.threads.com/@vanrah.sama"
        ]
    };

    let pageSchema = null;

    if (page === "home") {
        pageSchema = personSchema;
    }

    else if ((page === "mangas" || page === "works") && title_path) {
        pageSchema = {
            "@context": "https://schema.org",
            "@type": page === "mangas" ? "ComicSeries" : "CreativeWork",
            "@id": `${currentUrl}#work`,
            "name": title_name,
            "description": description,
            "url": currentUrl,
            "inLanguage": lang,
            "image": `${baseUrl}/og-image.png`,
            "author": {
                "@type": "Person",
                "@id": `${baseUrl}#person`
            }
        };
    }

    else if (page === "articles" && title_path) {
        pageSchema = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": `${currentUrl}#article`,
            "headline": title_name,
            "description": description,
            "url": currentUrl,
            "inLanguage": lang,
            "author": {
                "@type": "Person",
                "@id": `${baseUrl}#person`
            }
        };
    }

    let breadcrumbSchema = null;

    if (title_path) {
        breadcrumbSchema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${currentUrl}#breadcrumb`,
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": page.charAt(0).toUpperCase() + page.slice(1),
                    "item": `${baseUrl}/${lang}/${page}`
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": title_name,
                    "item": currentUrl
                }
            ]
        };
    }

    return (
        <Helmet>
            <html lang={lang} />

            <title>{title}</title>
            <meta name="description" content={description} />

            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={`${baseUrl}/og-image.png`} />
            <meta property="og:url" content={currentUrl} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />

            <link rel="canonical" href={currentUrl} />

            <link rel="alternate" hrefLang="fr" href={`${baseUrl}/fr/${page}${title_path ? "/" + title_path : ""}`} />
            <link rel="alternate" hrefLang="en" href={`${baseUrl}/en/${page}${title_path ? "/" + title_path : ""}`} />

            {pageSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(pageSchema)}
                </script>
            )}

            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}
        </Helmet>
    );
};

export default Seo;